import { AlertTriangle, Eye, Mail } from 'lucide-react'
import type { ProposalDeliveryDto } from '@lib/dto/proposal-delivery.dto'
import { formatDateTime } from '@lib/helpers/format'

/**
 * What actually happened to a sent version.
 *
 * This is the answer to the question that had none: an admin marked a proposal
 * sent, the client never received it, and there was nothing to consult. Every
 * attempt is listed with where it went and whether it was opened.
 *
 * Two states are deliberately called out rather than left to be inferred:
 *
 * - A version sent with no delivery at all, which every version from before
 *   this existed is, and which means somebody sent it by hand — or believed
 *   the old Send button had.
 * - A delivery that only reached the log because no mail provider is
 *   configured. Showing that as "sent" is precisely the failure this feature
 *   exists to remove.
 */
export function ProposalDeliveryList({
  deliveries,
  isSent,
}: {
  deliveries: ProposalDeliveryDto[]
  isSent: boolean
}) {
  if (!isSent) return null

  if (deliveries.length === 0) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-muted-text">
        <Mail className="mt-0.5 h-3 w-3 shrink-0" />
        <span>
          Marked as sent by hand — nothing was emailed from here, so there is
          no record of it arriving.
        </span>
      </p>
    )
  }

  return (
    <ul className="space-y-1.5">
      {deliveries.map((delivery) => (
        <li key={delivery.id} className="flex items-start gap-1.5 text-xs">
          {delivery.disposition === 'LOGGED' ? (
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-warning" />
          ) : (
            <Mail className="mt-0.5 h-3 w-3 shrink-0 text-muted-text" />
          )}
          <span className="text-muted-text">
            {delivery.disposition === 'LOGGED' ? (
              <span className="font-medium text-warning">
                Not delivered — no mail provider configured.{' '}
              </span>
            ) : null}
            Emailed to{' '}
            <span className="text-secondary-text">{delivery.toEmail}</span>{' '}
            {formatDateTime(delivery.createdAt)}
            {delivery.sentByName === null ? '' : ` by ${delivery.sentByName}`}
            {delivery.firstViewedAt === null ? (
              <span className="text-muted-text"> · not opened yet</span>
            ) : (
              <>
                {' · '}
                <span className="inline-flex items-center gap-1 text-secondary-text">
                  <Eye className="h-3 w-3" />
                  {/*
                    When, not how often. The panel below counts the client's
                    visits; a second count here, of page loads, disagreed
                    with it on the same card.
                  */}
                  opened {formatDateTime(delivery.firstViewedAt)}
                </span>
              </>
            )}
          </span>
        </li>
      ))}
    </ul>
  )
}
